import React, { useState, useEffect, useCallback } from "react"
import {
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    IconButton,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import AddIcon from "@mui/icons-material/Add"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import * as adminService from "../../services/adminService"

const PricingAdminPage: React.FC = () => {
    const { t } = useTranslation()
    const { companyId } = useParams<{ companyId: string }>()
    const [tabIndex, setTabIndex] = useState(0)
    const [priceGrids, setPriceGrids] = useState<any[]>([])
    const [pricingRules, setPricingRules] = useState<any[]>([])
    const [selectedGridId, setSelectedGridId] = useState<string | null>(null)

    const [openGridDialog, setOpenGridDialog] = useState(false)
    const [editingGrid, setEditingGrid] = useState<any>(null)
    const [gridFormData, setGridFormData] = useState({
        name: "",
        scope: "company",
        outlet_id: "",
        is_active: true,
    })

    const [openRuleDialog, setOpenRuleDialog] = useState(false)
    const [editingRule, setEditingRule] = useState<any>(null)
    const [ruleFormData, setRuleFormData] = useState({
        price_grid_id: "",
        item_id: "",
        customer_id: "",
        unit_price_minor: 0,
        currency: "EUR",
        price_type: "base",
        effective_from: "",
        effective_to: "",
    })

    const loadPriceGrids = useCallback(async () => {
        if (!companyId) return
        try {
            const data = await adminService.getPriceGrids(companyId)
            setPriceGrids(data)
        } catch (error) {
            console.error("Failed to load price grids:", error)
        }
    }, [companyId])

    const loadPricingRules = useCallback(
        async (priceGridId: string) => {
            if (!companyId) return
            try {
                const data = await adminService.getPricingRules(
                    companyId,
                    priceGridId
                )
                setPricingRules(data)
            } catch (error) {
                console.error("Failed to load pricing rules:", error)
            }
        },
        [companyId]
    )

    useEffect(() => {
        loadPriceGrids()
    }, [loadPriceGrids])

    useEffect(() => {
        if (selectedGridId) {
            loadPricingRules(selectedGridId)
        }
    }, [selectedGridId, loadPricingRules])

    const handleOpenGridDialog = (grid?: any) => {
        if (grid) {
            setEditingGrid(grid)
            setGridFormData({
                name: grid.name,
                scope: grid.scope,
                outlet_id: grid.outlet_id || "",
                is_active: grid.is_active,
            })
        } else {
            setEditingGrid(null)
            setGridFormData({
                name: "",
                scope: "company",
                outlet_id: "",
                is_active: true,
            })
        }
        setOpenGridDialog(true)
    }

    const handleCloseGridDialog = () => {
        setOpenGridDialog(false)
        setEditingGrid(null)
    }

    const handleSaveGrid = async () => {
        if (!companyId) return
        try {
            if (editingGrid) {
                await adminService.updatePriceGrid(
                    companyId,
                    editingGrid.id,
                    gridFormData
                )
            } else {
                await adminService.createPriceGrid(companyId, gridFormData)
            }
            handleCloseGridDialog()
            loadPriceGrids()
        } catch (error) {
            console.error("Failed to save price grid:", error)
        }
    }

    const handleDeleteGrid = async (gridId: string) => {
        if (!companyId) return
        try {
            await adminService.deletePriceGrid(companyId, gridId)
            loadPriceGrids()
        } catch (error) {
            console.error("Failed to delete price grid:", error)
        }
    }

    const handleOpenRuleDialog = (rule?: any) => {
        if (rule) {
            setEditingRule(rule)
            setRuleFormData({
                price_grid_id: rule.price_grid_id,
                item_id: rule.item_id,
                customer_id: rule.customer_id || "",
                unit_price_minor: rule.unit_price_minor,
                currency: rule.currency,
                price_type: rule.price_type,
                effective_from: rule.effective_from || "",
                effective_to: rule.effective_to || "",
            })
        } else {
            setEditingRule(null)
            setRuleFormData({
                price_grid_id: selectedGridId || "",
                item_id: "",
                customer_id: "",
                unit_price_minor: 0,
                currency: "EUR",
                price_type: "base",
                effective_from: "",
                effective_to: "",
            })
        }
        setOpenRuleDialog(true)
    }

    const handleCloseRuleDialog = () => {
        setOpenRuleDialog(false)
        setEditingRule(null)
    }

    const handleSaveRule = async () => {
        if (!companyId) return
        try {
            if (editingRule) {
                await adminService.updatePricingRule(
                    companyId,
                    editingRule.id,
                    ruleFormData
                )
            } else {
                await adminService.createPricingRule(companyId, ruleFormData)
            }
            handleCloseRuleDialog()
            if (selectedGridId) {
                loadPricingRules(selectedGridId)
            }
        } catch (error) {
            console.error("Failed to save pricing rule:", error)
        }
    }

    const handleDeleteRule = async (ruleId: string) => {
        if (!companyId) return
        try {
            await adminService.deletePricingRule(companyId, ruleId)
            if (selectedGridId) {
                loadPricingRules(selectedGridId)
            }
        } catch (error) {
            console.error("Failed to delete pricing rule:", error)
        }
    }

    return (
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
            <Container maxWidth="lg">
                <Typography variant="h4" gutterBottom>
                    {t("pricingAdmin.title")}
                </Typography>

                <Tabs
                    value={tabIndex}
                    onChange={(e, v) => setTabIndex(v)}
                    sx={{ mb: 3 }}
                >
                    <Tab label={t("pricingAdmin.tabs.priceGrids")} />
                    <Tab
                        label={t("pricingAdmin.tabs.pricingRules")}
                        disabled={!selectedGridId}
                    />
                </Tabs>

                {tabIndex === 0 && (
                    <>
                        <Box
                            sx={{
                                mb: 3,
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenGridDialog()}
                            >
                                {t("pricingAdmin.actions.addPriceGrid")}
                            </Button>
                        </Box>

                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            {t(
                                                "pricingAdmin.grids.columns.name"
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {t(
                                                "pricingAdmin.grids.columns.scope"
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {t(
                                                "pricingAdmin.grids.columns.outletId"
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {t(
                                                "pricingAdmin.grids.columns.status"
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {t(
                                                "pricingAdmin.grids.columns.actions"
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {priceGrids.map((grid) => (
                                        <TableRow
                                            key={grid.id}
                                            selected={
                                                selectedGridId === grid.id
                                            }
                                            onClick={() =>
                                                setSelectedGridId(grid.id)
                                            }
                                            sx={{ cursor: "pointer" }}
                                        >
                                            <TableCell>{grid.name}</TableCell>
                                            <TableCell>{grid.scope}</TableCell>
                                            <TableCell>
                                                {grid.outlet_id ||
                                                    t("common.dash")}
                                            </TableCell>
                                            <TableCell>
                                                {grid.is_active
                                                    ? t(
                                                          "pricingAdmin.status.active"
                                                      )
                                                    : t(
                                                          "pricingAdmin.status.inactive"
                                                      )}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleOpenGridDialog(
                                                            grid
                                                        )
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleDeleteGrid(
                                                            grid.id
                                                        )
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}

                {tabIndex === 1 && selectedGridId && (
                    <>
                        <Box
                            sx={{
                                mb: 3,
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenRuleDialog()}
                            >
                                {t("pricingAdmin.actions.addPricingRule")}
                            </Button>
                        </Box>

                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            {t(
                                                "pricingAdmin.rules.columns.itemId"
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {t(
                                                "pricingAdmin.rules.columns.customerId"
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {t(
                                                "pricingAdmin.rules.columns.unitPrice"
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {t(
                                                "pricingAdmin.rules.columns.type"
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {t(
                                                "pricingAdmin.rules.columns.effectiveFrom"
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {t(
                                                "pricingAdmin.rules.columns.effectiveTo"
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {t(
                                                "pricingAdmin.rules.columns.actions"
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pricingRules.map((rule) => (
                                        <TableRow key={rule.id}>
                                            <TableCell>
                                                {rule.item_id}
                                            </TableCell>
                                            <TableCell>
                                                {rule.customer_id ||
                                                    t("common.dash")}
                                            </TableCell>
                                            <TableCell>
                                                {(
                                                    rule.unit_price_minor / 100
                                                ).toFixed(2)}{" "}
                                                {rule.currency}
                                            </TableCell>
                                            <TableCell>
                                                {rule.price_type}
                                            </TableCell>
                                            <TableCell>
                                                {rule.effective_from ||
                                                    t("common.dash")}
                                            </TableCell>
                                            <TableCell>
                                                {rule.effective_to ||
                                                    t("common.dash")}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    onClick={() =>
                                                        handleOpenRuleDialog(
                                                            rule
                                                        )
                                                    }
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() =>
                                                        handleDeleteRule(
                                                            rule.id
                                                        )
                                                    }
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}

                <Dialog
                    open={openGridDialog}
                    onClose={handleCloseGridDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        {editingGrid
                            ? t("pricingAdmin.gridDialog.editTitle")
                            : t("pricingAdmin.gridDialog.addTitle")}
                    </DialogTitle>
                    <DialogContent>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                mt: 1,
                            }}
                        >
                            <TextField
                                label={String(
                                    t("pricingAdmin.gridDialog.fields.name")
                                )}
                                required
                                fullWidth
                                value={gridFormData.name}
                                onChange={(e) =>
                                    setGridFormData({
                                        ...gridFormData,
                                        name: e.target.value,
                                    })
                                }
                            />
                            <FormControl fullWidth required>
                                <InputLabel>
                                    {String(
                                        t(
                                            "pricingAdmin.gridDialog.fields.scope"
                                        )
                                    )}
                                </InputLabel>
                                <Select
                                    value={gridFormData.scope}
                                    label={String(
                                        t(
                                            "pricingAdmin.gridDialog.fields.scope"
                                        )
                                    )}
                                    onChange={(e) =>
                                        setGridFormData({
                                            ...gridFormData,
                                            scope: e.target.value,
                                        })
                                    }
                                >
                                    <MenuItem value="company">
                                        {t("pricingAdmin.scopes.company")}
                                    </MenuItem>
                                    <MenuItem value="outlet">
                                        {t("pricingAdmin.scopes.outlet")}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            {gridFormData.scope === "outlet" && (
                                <TextField
                                    label={String(
                                        t(
                                            "pricingAdmin.gridDialog.fields.outletId"
                                        )
                                    )}
                                    required
                                    fullWidth
                                    value={gridFormData.outlet_id}
                                    onChange={(e) =>
                                        setGridFormData({
                                            ...gridFormData,
                                            outlet_id: e.target.value,
                                        })
                                    }
                                />
                            )}
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={gridFormData.is_active}
                                        onChange={(e) =>
                                            setGridFormData({
                                                ...gridFormData,
                                                is_active: e.target.checked,
                                            })
                                        }
                                    />
                                }
                                label={t("pricingAdmin.status.active")}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseGridDialog}>
                            {t("common.cancel")}
                        </Button>
                        <Button onClick={handleSaveGrid} variant="contained">
                            {t("common.save")}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={openRuleDialog}
                    onClose={handleCloseRuleDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        {editingRule
                            ? t("pricingAdmin.ruleDialog.editTitle")
                            : t("pricingAdmin.ruleDialog.addTitle")}
                    </DialogTitle>
                    <DialogContent>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                mt: 1,
                            }}
                        >
                            <TextField
                                label={String(
                                    t("pricingAdmin.ruleDialog.fields.itemId")
                                )}
                                required
                                fullWidth
                                value={ruleFormData.item_id}
                                onChange={(e) =>
                                    setRuleFormData({
                                        ...ruleFormData,
                                        item_id: e.target.value,
                                    })
                                }
                            />
                            <TextField
                                label={String(
                                    t(
                                        "pricingAdmin.ruleDialog.fields.customerIdOptional"
                                    )
                                )}
                                fullWidth
                                value={ruleFormData.customer_id}
                                onChange={(e) =>
                                    setRuleFormData({
                                        ...ruleFormData,
                                        customer_id: e.target.value,
                                    })
                                }
                            />
                            <TextField
                                label={String(
                                    t(
                                        "pricingAdmin.ruleDialog.fields.unitPriceMinor"
                                    )
                                )}
                                type="number"
                                required
                                fullWidth
                                value={ruleFormData.unit_price_minor}
                                onChange={(e) =>
                                    setRuleFormData({
                                        ...ruleFormData,
                                        unit_price_minor: parseInt(
                                            e.target.value
                                        ),
                                    })
                                }
                            />
                            <TextField
                                label={String(
                                    t("pricingAdmin.ruleDialog.fields.currency")
                                )}
                                required
                                fullWidth
                                value={ruleFormData.currency}
                                onChange={(e) =>
                                    setRuleFormData({
                                        ...ruleFormData,
                                        currency: e.target.value,
                                    })
                                }
                            />
                            <FormControl fullWidth required>
                                <InputLabel>
                                    {String(
                                        t(
                                            "pricingAdmin.ruleDialog.fields.priceType"
                                        )
                                    )}
                                </InputLabel>
                                <Select
                                    value={ruleFormData.price_type}
                                    label={String(
                                        t(
                                            "pricingAdmin.ruleDialog.fields.priceType"
                                        )
                                    )}
                                    onChange={(e) =>
                                        setRuleFormData({
                                            ...ruleFormData,
                                            price_type: e.target.value,
                                        })
                                    }
                                >
                                    <MenuItem value="base">
                                        {t("pricingAdmin.priceTypes.base")}
                                    </MenuItem>
                                    <MenuItem value="promo">
                                        {t("pricingAdmin.priceTypes.promo")}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label={String(
                                    t(
                                        "pricingAdmin.ruleDialog.fields.effectiveFromOptional"
                                    )
                                )}
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={ruleFormData.effective_from}
                                onChange={(e) =>
                                    setRuleFormData({
                                        ...ruleFormData,
                                        effective_from: e.target.value,
                                    })
                                }
                            />
                            <TextField
                                label={String(
                                    t(
                                        "pricingAdmin.ruleDialog.fields.effectiveToOptional"
                                    )
                                )}
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={ruleFormData.effective_to}
                                onChange={(e) =>
                                    setRuleFormData({
                                        ...ruleFormData,
                                        effective_to: e.target.value,
                                    })
                                }
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseRuleDialog}>
                            {t("common.cancel")}
                        </Button>
                        <Button onClick={handleSaveRule} variant="contained">
                            {t("common.save")}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    )
}

export default PricingAdminPage
